import React from 'react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Mail, Phone, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-navy-dark border-b border-gold/20 py-6">
        <div className="container mx-auto px-6">
          <Button
            variant="ghost"
            className="text-gold hover:text-gold-light hover:bg-gold/10"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="mr-2 w-5 h-5" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-16 max-w-5xl">
        <div className="bg-white rounded-none shadow-2xl p-12 md:p-16">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-navy mb-4">
              Privacy Policy
            </h1>
            <div className="h-1 w-24 bg-gold mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg">
              The Profit Prophet (Pty) Ltd
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Last Updated: December 2025
            </p>
          </div>

          <Separator className="my-12" />

          {/* 1. Introduction */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Profit Prophet ("we," "us," or "our") is committed to protecting the privacy and 
              security of your personal information. This Privacy Policy explains how we collect, use, 
              store, and protect your personal data when you visit our website at{' '}
              <a href="https://www.theprofitprophet.co.za" className="text-gold hover:text-gold-light font-semibold">
                www.theprofitprophet.co.za
              </a>{' '}
              or engage with our services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We operate in full compliance with the <strong>Protection of Personal Information Act (POPIA), 
              Act 4 of 2013</strong>, and adhere to internationally recognized data protection principles to 
              ensure transparency, accountability, and respect for your privacy rights.
            </p>
          </section>

          {/* 2. Information We Collect */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">2. Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may collect the following categories of personal information:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-gold">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-gold mr-3 font-bold">•</span>
                  <span><strong>Contact Information:</strong> Full name, email address, phone number, business name, and job title</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 font-bold">•</span>
                  <span><strong>Business Information:</strong> Company details, industry, service requirements, and project specifications</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 font-bold">•</span>
                  <span><strong>Communication Data:</strong> Information submitted through contact forms, email correspondence, WhatsApp messages, or phone calls</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 font-bold">•</span>
                  <span><strong>Technical Data:</strong> IP address, browser type, device information, operating system, and referring website</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 font-bold">•</span>
                  <span><strong>Usage Data:</strong> Pages visited, time spent on site, navigation patterns, and interaction with website features</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 font-bold">•</span>
                  <span><strong>Cookie Data:</strong> Information collected through cookies and similar tracking technologies (see Section 10)</span>
                </li>
              </ul>
            </div>
          </section>

          {/* 3. How Information Is Collected */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">3. How Information Is Collected</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We collect personal information through the following channels:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-navy-light/5 p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-navy mb-3">Direct Collection</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Contact forms on our website</li>
                  <li>• Email communications</li>
                  <li>• WhatsApp inquiries</li>
                  <li>• Phone consultations</li>
                  <li>• Service booking requests</li>
                </ul>
              </div>
              <div className="bg-navy-light/5 p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-navy mb-3">Automated Collection</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Website analytics tools</li>
                  <li>• Cookies and tracking pixels</li>
                  <li>• Server logs</li>
                  <li>• Browser information</li>
                  <li>• Session recordings (aggregated)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 4. Purpose of Data Collection */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">4. Purpose of Data Collection</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We collect and process your personal information for the following legitimate business purposes:
            </p>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="bg-gold text-navy font-bold w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                  1
                </div>
                <div>
                  <h3 className="text-lg font-bold text-navy mb-1">Service Delivery</h3>
                  <p className="text-gray-700">Responding to inquiries, providing consultations, delivering contracted services, and project management</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-gold text-navy font-bold w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                  2
                </div>
                <div>
                  <h3 className="text-lg font-bold text-navy mb-1">Communication</h3>
                  <p className="text-gray-700">Sending service updates, responding to requests, scheduling consultations, and providing customer support</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-gold text-navy font-bold w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                  3
                </div>
                <div>
                  <h3 className="text-lg font-bold text-navy mb-1">Website Optimization</h3>
                  <p className="text-gray-700">Analyzing website performance, improving user experience, fixing technical issues, and enhancing functionality</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-gold text-navy font-bold w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                  4
                </div>
                <div>
                  <h3 className="text-lg font-bold text-navy mb-1">Marketing Communications</h3>
                  <p className="text-gray-700">Sending newsletters, service updates, and promotional content (only with your explicit consent, which can be withdrawn at any time)</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-gold text-navy font-bold w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                  5
                </div>
                <div>
                  <h3 className="text-lg font-bold text-navy mb-1">Business Analytics</h3>
                  <p className="text-gray-700">Understanding client needs, market trends, service effectiveness, and strategic business planning</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-gold text-navy font-bold w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                  6
                </div>
                <div>
                  <h3 className="text-lg font-bold text-navy mb-1">Legal Compliance</h3>
                  <p className="text-gray-700">Meeting regulatory requirements, maintaining business records, and fulfilling contractual obligations</p>
                </div>
              </div>
            </div>
          </section>

          {/* 5. Legal Basis for Processing */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">5. Legal Basis for Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Under POPIA, we process your personal information based on one or more of the following lawful grounds:
            </p>
            <div className="bg-navy-light/5 p-8 rounded-lg border-l-4 border-gold">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-navy mb-2">Consent</h3>
                  <p className="text-gray-700">
                    You have provided explicit, informed, and voluntary consent for specific purposes 
                    (e.g., subscribing to marketing communications, submitting inquiry forms)
                  </p>
                </div>
                <Separator />
                <div>
                  <h3 className="text-xl font-bold text-navy mb-2">Contractual Necessity</h3>
                  <p className="text-gray-700">
                    Processing is necessary to fulfill a contract with you or to take steps at your 
                    request before entering into a contract (e.g., providing consulting services)
                  </p>
                </div>
                <Separator />
                <div>
                  <h3 className="text-xl font-bold text-navy mb-2">Legitimate Interests</h3>
                  <p className="text-gray-700">
                    Processing serves our legitimate business interests (e.g., improving services, 
                    preventing fraud, maintaining security) while respecting your rights and freedoms
                  </p>
                </div>
                <Separator />
                <div>
                  <h3 className="text-xl font-bold text-navy mb-2">Legal Obligation</h3>
                  <p className="text-gray-700">
                    Processing is required to comply with South African legal or regulatory requirements
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 6. Data Sharing */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">6. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell, rent, or trade your personal information to third parties for marketing purposes. 
              However, we may share your data with trusted service providers under strict confidentiality agreements:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-bold text-navy mb-4">Third-Party Service Providers:</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-gold mr-3 font-bold">•</span>
                  <span><strong>Web Hosting Providers:</strong> For website infrastructure and data storage</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 font-bold">•</span>
                  <span><strong>Analytics Platforms:</strong> To measure website performance and user behavior (e.g., Google Analytics)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 font-bold">•</span>
                  <span><strong>Communication Tools:</strong> Email service providers, messaging platforms, and CRM systems</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 font-bold">•</span>
                  <span><strong>Payment Processors:</strong> For secure transaction processing (if applicable)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 font-bold">•</span>
                  <span><strong>Professional Advisors:</strong> Legal, accounting, and consulting firms when necessary for business operations</span>
                </li>
              </ul>
            </div>
            <div className="bg-gold/10 border-l-4 border-gold p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-navy">Important:</strong> All third-party service providers are 
                contractually obligated to process your data only for specified purposes, maintain appropriate 
                security measures, and comply with applicable data protection laws. We conduct due diligence 
                to ensure they meet our privacy and security standards.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mt-6">
              We may also disclose personal information if required by law, court order, government authority, 
              or to protect our legal rights, prevent fraud, or ensure the safety of our clients and employees.
            </p>
          </section>

          {/* 7. Data Security */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">7. Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement reasonable technical, administrative, and organizational security measures to 
              protect your personal information from unauthorized access, disclosure, alteration, or destruction:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-navy-light/5 p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-bold text-navy mb-3">Technical Safeguards</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• SSL/TLS encryption for data transmission</li>
                  <li>• Secure server infrastructure</li>
                  <li>• Firewalls and intrusion detection</li>
                  <li>• Regular security updates and patches</li>
                  <li>• Encrypted data storage where appropriate</li>
                </ul>
              </div>
              <div className="bg-navy-light/5 p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-bold text-navy mb-3">Administrative Controls</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Access controls and authentication</li>
                  <li>• Employee confidentiality agreements</li>
                  <li>• Privacy and security training</li>
                  <li>• Incident response procedures</li>
                  <li>• Regular security audits</li>
                </ul>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mt-6 italic">
              While we strive to protect your personal information, no method of electronic transmission or 
              storage is 100% secure. We cannot guarantee absolute security but are committed to maintaining 
              industry-standard protections and continuously improving our security posture.
            </p>
          </section>

          {/* 8. Data Retention */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">8. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain your personal information only for as long as necessary to fulfill the purposes 
              outlined in this Privacy Policy, or as required by law:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-navy-light">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-navy mr-3 font-bold">•</span>
                  <span><strong>Active Client Data:</strong> Retained for the duration of the business relationship and up to 7 years thereafter for legal and accounting purposes</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy mr-3 font-bold">•</span>
                  <span><strong>Inquiry and Contact Data:</strong> Retained for up to 2 years from the last interaction, unless you request earlier deletion</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy mr-3 font-bold">•</span>
                  <span><strong>Marketing Communications:</strong> Retained until you withdraw consent or unsubscribe</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy mr-3 font-bold">•</span>
                  <span><strong>Website Analytics:</strong> Typically aggregated and anonymized after 26 months (subject to analytics platform policies)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-navy mr-3 font-bold">•</span>
                  <span><strong>Legal Records:</strong> Retained as required by South African law (e.g., tax, corporate, and financial regulations)</span>
                </li>
              </ul>
            </div>
            <p className="text-gray-700 leading-relaxed mt-4">
              Once the retention period expires, personal information is securely deleted or anonymized 
              in accordance with our data disposal procedures.
            </p>
          </section>

          {/* 9. User Rights (POPIA) */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">9. Your Rights Under POPIA</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Under the Protection of Personal Information Act, you have the following rights regarding your personal data:
            </p>
            <div className="space-y-4">
              <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-gold transition-colors">
                <h3 className="text-lg font-bold text-navy mb-2">Right to Access</h3>
                <p className="text-gray-700">Request confirmation of whether we hold your personal information and obtain a copy of it</p>
              </div>
              <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-gold transition-colors">
                <h3 className="text-lg font-bold text-navy mb-2">Right to Correction</h3>
                <p className="text-gray-700">Request correction of inaccurate, incomplete, or outdated personal information</p>
              </div>
              <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-gold transition-colors">
                <h3 className="text-lg font-bold text-navy mb-2">Right to Deletion (Erasure)</h3>
                <p className="text-gray-700">Request deletion of your personal information, subject to legal and contractual retention requirements</p>
              </div>
              <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-gold transition-colors">
                <h3 className="text-lg font-bold text-navy mb-2">Right to Object</h3>
                <p className="text-gray-700">Object to processing of your personal information for direct marketing purposes or based on legitimate interests</p>
              </div>
              <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-gold transition-colors">
                <h3 className="text-lg font-bold text-navy mb-2">Right to Withdraw Consent</h3>
                <p className="text-gray-700">Withdraw consent for processing at any time (without affecting the lawfulness of prior processing)</p>
              </div>
              <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-gold transition-colors">
                <h3 className="text-lg font-bold text-navy mb-2">Right to Lodge a Complaint</h3>
                <p className="text-gray-700">File a complaint with the Information Regulator of South Africa if you believe your rights have been violated</p>
              </div>
            </div>
            <div className="bg-gold/10 border-l-4 border-gold p-6 rounded-lg mt-6">
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-navy">To Exercise Your Rights:</strong> Please contact us using the 
                details in Section 13. We will respond to verified requests within 30 days, or as required by law. 
                We may request additional information to verify your identity before processing your request.
              </p>
            </div>
          </section>

          {/* 10. Cookies Policy Summary */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">10. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our website uses cookies and similar technologies to enhance user experience, analyze website 
              traffic, and improve functionality.
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-bold text-navy mb-4">Types of Cookies We Use:</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-navy mb-1">Essential Cookies</h4>
                  <p className="text-gray-700 text-sm">Required for website functionality, security, and session management. Cannot be disabled.</p>
                </div>
                <div>
                  <h4 className="font-bold text-navy mb-1">Analytics Cookies</h4>
                  <p className="text-gray-700 text-sm">Track website usage, visitor behavior, and performance metrics (e.g., Google Analytics) to improve services.</p>
                </div>
                <div>
                  <h4 className="font-bold text-navy mb-1">Functional Cookies</h4>
                  <p className="text-gray-700 text-sm">Remember user preferences and settings for enhanced experience.</p>
                </div>
              </div>
            </div>
            <div className="bg-navy-light/5 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-bold text-navy mb-3">Managing Cookies</h3>
              <p className="text-gray-700 mb-3">
                You can control and manage cookies through your browser settings. Most browsers allow you to:
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Block all cookies</li>
                <li>• Block third-party cookies</li>
                <li>• Delete cookies after closing your browser</li>
                <li>• Receive notifications when cookies are set</li>
              </ul>
              <p className="text-gray-700 text-sm mt-3 italic">
                Note: Disabling cookies may affect website functionality and user experience.
              </p>
            </div>
          </section>

          {/* 11. Third-Party Links Disclaimer */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">11. Third-Party Links and Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our website may contain links to third-party websites, platforms, or services (e.g., social media, 
              external resources, partner sites) for your convenience and information.
            </p>
            <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong className="text-red-600">Important Disclaimer:</strong> We are not responsible for 
                the privacy practices, content, or security of third-party websites. These external sites operate 
                independently and have their own privacy policies and terms of use.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We strongly encourage you to review the privacy policies of any third-party website before 
                providing personal information. Your interactions with external sites are at your own risk.
              </p>
            </div>
          </section>

          {/* 12. Updates to Policy */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">12. Updates and Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update this Privacy Policy periodically to reflect changes in our practices, technology, 
              legal requirements, or business operations. When we make material changes, we will:
            </p>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li className="flex items-start">
                <span className="text-gold mr-3 font-bold">•</span>
                <span>Update the "Last Updated" date at the top of this page</span>
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-3 font-bold">•</span>
                <span>Notify active clients via email or through our website</span>
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-3 font-bold">•</span>
                <span>Provide a reasonable notice period before changes take effect (where appropriate)</span>
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Your continued use of our website and services after the updated Privacy Policy takes effect 
              constitutes acceptance of the changes. We encourage you to review this policy regularly to 
              stay informed about how we protect your information.
            </p>
          </section>

          {/* 13. Contact Information */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-navy mb-6">13. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data 
              practices, please contact us:
            </p>
            <div className="bg-navy-dark text-white p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-gold mb-6">The Profit Prophet</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Mail className="w-6 h-6 text-gold mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm">Email</p>
                    <a href="mailto:info@theprofitprophet.co.za" className="text-white hover:text-gold text-lg font-semibold">
                      info@theprofitprophet.co.za
                    </a>
                  </div>
                </div>
                <Separator className="bg-gold/20" />
                <div className="flex items-start">
                  <Phone className="w-6 h-6 text-gold mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm">Phone</p>
                    <a href="tel:0659994443" className="text-white hover:text-gold text-lg font-semibold">
                      065 999 4443
                    </a>
                  </div>
                </div>
                <Separator className="bg-gold/20" />
                <div className="flex items-start">
                  <Globe className="w-6 h-6 text-gold mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm">Website</p>
                    <a href="https://www.theprofitprophet.co.za" target="_blank" rel="noopener noreferrer" className="text-white hover:text-gold text-lg font-semibold">
                      www.theprofitprophet.co.za
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-gold/20">
                <p className="text-gray-400 text-sm leading-relaxed">
                  <strong className="text-gold">Privacy Requests:</strong> For data access, correction, deletion, 
                  or other privacy-related requests, please email us with "Privacy Request" in the subject line. 
                  We will respond within 30 days.
                </p>
              </div>
            </div>
          </section>

          {/* Information Regulator Contact */}
          <section className="mb-12">
            <div className="bg-gray-50 p-8 rounded-lg border-2 border-gray-200">
              <h3 className="text-xl font-bold text-navy mb-4">Information Regulator of South Africa</h3>
              <p className="text-gray-700 mb-4">
                If you are not satisfied with our response to your privacy concerns, you have the right to 
                lodge a complaint with:
              </p>
              <div className="text-gray-700 leading-relaxed">
                <p className="font-semibold">The Information Regulator (South Africa)</p>
                <p>JD House, 27 Stiemens Street</p>
                <p>Braamfontein, Johannesburg, 2001</p>
                <p className="mt-2">
                  Email: <a href="mailto:inforeg@justice.gov.za" className="text-gold hover:text-gold-light font-semibold">inforeg@justice.gov.za</a>
                </p>
                <p>
                  Website: <a href="https://www.justice.gov.za/inforeg/" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-gold-light font-semibold">www.justice.gov.za/inforeg</a>
                </p>
              </div>
            </div>
          </section>

          {/* Closing Statement */}
          <Separator className="my-12" />
          <div className="text-center">
            <p className="text-gray-600 italic">
              Thank you for trusting The Profit Prophet with your information. We are committed to protecting 
              your privacy and maintaining the highest standards of data protection.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-navy-dark py-8 border-t border-gold/20">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} The Profit Prophet. All rights reserved.
          </p>
          
          <Separator className="my-6 bg-gold/10 max-w-2xl mx-auto" />
          
          <div className="mt-6 max-w-3xl mx-auto">
            <p className="text-gray-500 text-xs font-semibold mb-3 uppercase tracking-wider">Legal / Business Information</p>
            <p className="text-gray-400 text-xs leading-relaxed mb-2">
              The Profit Prophet is a pricing and profitability advisory platform founded and operated by Rohan Anson Basson, 
              based in Johannesburg, South Africa.
            </p>
            <p className="text-gray-400 text-xs leading-relaxed">
              The platform provides AI-powered pricing assistance for contractors, service businesses, and SMEs 
              looking to structure profitable quotes.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
